import { SYNC_STATE } from "./offline-queue-core.js";

export const SYNCED_MEDIA_RETENTION_DAYS = 30;
export const SYNCED_MEDIA_RETENTION_MS = SYNCED_MEDIA_RETENTION_DAYS * 24 * 60 * 60 * 1000;
export const STORAGE_HIGH_WATERMARK = 0.92;
export const STORAGE_MINIMUM_RESERVE_BYTES = 10 * 1024 * 1024;

function usableBlob(value) {
  return value instanceof Blob && Number(value.size || 0) > 0;
}

export function hasRetainedMediaBlob(item = {}) {
  return usableBlob(item.file) || usableBlob(item.originalFile);
}

export function retainedMediaBytes(item = {}) {
  const stamped = usableBlob(item.file) ? Number(item.file.size || 0) : 0;
  const original = usableBlob(item.originalFile) ? Number(item.originalFile.size || 0) : 0;
  return stamped + original;
}

export function isSyncedMediaBlobCleanupEligible(
  item = {},
  { now = Date.now(), retentionMs = SYNCED_MEDIA_RETENTION_MS } = {},
) {
  if (String(item.syncStatus || "").toUpperCase() !== SYNC_STATE.SYNCED) return false;
  if (!item.serverMediaId || !hasRetainedMediaBlob(item)) return false;
  const confirmedAt = new Date(item.syncedAt || 0).getTime();
  return Number.isFinite(confirmedAt) && confirmedAt > 0 && confirmedAt + retentionMs <= Number(now);
}

export function evaluateStorageCapacity(
  { usage = null, quota = null, incomingBytes = 0 } = {},
) {
  const safeUsage = Math.max(0, Number(usage || 0));
  const safeQuota = Math.max(0, Number(quota || 0));
  const safeIncoming = Math.max(0, Number(incomingBytes || 0));
  if (!safeQuota) return { accepted: true, availableBytes: null, requiredReserveBytes: null };

  const availableBytes = Math.max(0, safeQuota - safeUsage);
  const requiredReserveBytes = Math.max(STORAGE_MINIMUM_RESERVE_BYTES, safeIncoming * 2);
  const projectedUsageRatio = (safeUsage + safeIncoming) / safeQuota;
  return {
    accepted: availableBytes >= safeIncoming + requiredReserveBytes
      && projectedUsageRatio <= STORAGE_HIGH_WATERMARK,
    availableBytes,
    requiredReserveBytes,
    projectedUsageRatio,
  };
}

export function summarizeRetainedMedia(items = [], now = Date.now()) {
  return items.reduce((summary, item) => {
    if (!hasRetainedMediaBlob(item)) return summary;
    const bytes = retainedMediaBytes(item);
    summary.retainedBlobCount += 1;
    summary.retainedBlobBytes += bytes;
    if (isSyncedMediaBlobCleanupEligible(item, { now })) {
      summary.cleanupEligibleCount += 1;
      summary.cleanupEligibleBytes += bytes;
    } else if (String(item.syncStatus || "").toUpperCase() !== SYNC_STATE.SYNCED) {
      summary.protectedBlobCount += 1;
      summary.protectedBlobBytes += bytes;
    }
    return summary;
  }, {
    retainedBlobCount: 0,
    retainedBlobBytes: 0,
    protectedBlobCount: 0,
    protectedBlobBytes: 0,
    cleanupEligibleCount: 0,
    cleanupEligibleBytes: 0,
  });
}
